import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Printer, Save } from "lucide-react";
import { api, errMsg, fullName, fmtDateTime } from "@/lib/api";
import { Field } from "@/components/Field";
import { DietPlanTable } from "@/components/DietPlanTable";

export function DietTab({ rec, reload }) {
  const pid = rec.patient.id;
  const [plan, setPlan] = useState(rec.diet_plan);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setPlan(rec.diet_plan); setDirty(false); }, [rec.diet_plan]);

  const change = (p) => { setPlan(p); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { title, start_date, days, col_notes, general_notes } = plan;
      const { data } = await api.put(`/patients/${pid}/diet`, { title, start_date, days, col_notes, general_notes });
      toast.success("Piano nutrizionale salvato", { description: data.visible ? "Il paziente è stato avvisato dell'aggiornamento" : "Il piano è nascosto al paziente" });
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (dirty) await save();
    try {
      const { data } = await api.put(`/patients/${pid}/diet/visibility`, { visible: !plan.visible });
      toast.success(data.visible ? "Piano visibile al paziente" : "Piano nascosto al paziente");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="no-print card flex flex-wrap items-end gap-4">
        <Field label="Titolo piano" className="min-w-[220px] flex-1">
          <input className="field" value={plan.title} onChange={(e) => change({ ...plan, title: e.target.value })} placeholder={fullName(rec.patient)} data-testid="diet-title-input" />
        </Field>
        <Field label="In vigore dal">
          <input type="date" className="field" value={plan.start_date || ""} onChange={(e) => change({ ...plan, start_date: e.target.value })} data-testid="diet-start-date-input" />
        </Field>
        <div className="flex flex-wrap gap-2">
          <button onClick={toggle} data-testid="diet-plan-toggle-visible"
            className={plan.visible ? "btn-primary" : "btn-dark"}>
            {plan.visible ? <><Eye className="h-4 w-4" /> Visibile al paziente</> : <><EyeOff className="h-4 w-4" /> Nascosto al paziente</>}
          </button>
          <button onClick={() => window.print()} className="btn-outline" data-testid="diet-print-button"><Printer className="h-4 w-4" /> Stampa / PDF</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="diet-save-button"><Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva piano"}</button>
        </div>
      </div>
      <div className="no-print flex items-center justify-between text-xs text-slate-500">
        <span data-testid="diet-visibility-status">{plan.visible ? "Il paziente può consultare questo piano nella sua area personale." : "Il piano non è visibile al paziente. Clicca il pulsante per renderlo visibile."}</span>
        <span>{dirty ? <span className="font-semibold text-amber-600">Modifiche non salvate</span> : plan.updated_at ? `Ultimo salvataggio ${fmtDateTime(plan.updated_at)}` : ""}</span>
      </div>
      <div className="no-print card">
        <DietPlanTable plan={plan} patientName={fullName(rec.patient)} editable onChange={change} prefix="diet" />
      </div>
      <div className="print-only print-area bg-white p-4">
        <DietPlanTable plan={plan} patientName={fullName(rec.patient)} prefix="diet-print" />
      </div>
    </div>
  );
}
